import {
  Coins,
  Clock4,
  Headphones,
  GraduationCap,
  Megaphone,
  Handshake,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const ASSOCIATE_TILES = [
  {
    icon: Coins,
    title: "Attractive Commission",
    desc: "Industry-leading payout structure with transparent slabs and bonuses.",
  },
  {
    icon: Clock4,
    title: "Timely Payouts",
    desc: "Get paid on time, every time — guaranteed weekly settlements.",
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    desc: "A relationship manager and back-office team to assist you 6 days a week.",
  },
  {
    icon: GraduationCap,
    title: "Training & Development",
    desc: "Continuous product workshops, sales playbooks and compliance training.",
  },
  {
    icon: Megaphone,
    title: "Digital Tools & Marketing",
    desc: "Co-branded creatives, lead-gen apps and CRM access to scale your business.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnership",
    desc: "Equity in our growth story — built on trust, transparency and shared success.",
  },
];

function Tile({ icon: Icon, title, desc, idx }) {
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <Reveal delay={idx * 70}>
      <div
        data-testid={`associate-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}
        onMouseMove={onMouseMove}
        className="tile h-full"
      >
        <div className="flex items-center justify-between">
          <div className="tile-icon">
            <Icon size={26} strokeWidth={1.75} />
          </div>
          <span className="font-display text-white/15 text-2xl">0{idx + 1}</span>
        </div>
        <h3 className="mt-5 font-display text-2xl text-white">{title}</h3>
        <p className="mt-2 text-white/65 text-sm leading-relaxed">{desc}</p>
        <div className="mt-5 h-px w-full bg-gradient-to-r from-[#D4AF37]/40 via-transparent to-transparent" />
        <div className="mt-4 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]/80">
          Join us →
        </div>
      </div>
    </Reveal>
  );
}

export default function AssociatesBanner() {
  return (
    <section
      id="associates"
      data-testid="associates-banner"
      className="relative banner-gold text-white py-24 md:py-32 overflow-hidden noise-overlay frame-corners"
    >
      {/* Decorative arcs */}
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[700px] h-[700px] rounded-full border border-[#D4AF37]/15" />
      <div className="pointer-events-none absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full border border-[#D4AF37]/10" />

      {/* Gold accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">
              Partner with Sahyog
            </span>
            <h2
              data-testid="associates-banner-title"
              className="font-display text-[40px] md:text-[64px] mt-5 leading-[1.05] tracking-tight"
            >
              For Our <em className="not-italic text-[#D4AF37]">Associates</em>
            </h2>
            <p className="mt-4 text-white/75 max-w-2xl mx-auto">
              Six powerful benefits that turn ambitious individuals into thriving financial
              entrepreneurs.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ASSOCIATE_TILES.map((t, i) => (
            <Tile key={t.title} {...t} idx={i} />
          ))}
        </div>

        <Reveal delay={200}>
          <div
            data-testid="associates-tagline"
            className="mt-16 md:mt-20 text-center"
          >
            <div className="gold-divider text-[10px] tracking-[0.4em] uppercase">Tagline</div>
            <p className="font-script italic text-3xl md:text-4xl text-[#F0D788] mt-5">
              Grow Together, Succeed Together
            </p>
            <div className="mt-4 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-white/85 text-sm md:text-base">
              <span>Partnership</span>
              <span className="text-[#D4AF37]">·</span>
              <span>Growth</span>
              <span className="text-[#D4AF37]">·</span>
              <span>Success</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={280}>
          <div className="mt-12 flex justify-center">
            <a
              data-testid="associates-cta-apply"
              href="/apply/dsa"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold text-sm font-semibold"
            >
              Apply to be an Associate
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
